import Navigo from 'navigo';
import { FeedbackQuestionsPage, listUpdatePage, SettingsPage } from '../pages';
import {
	useNamespace,
	SellerNotificationsV2,
	SellerCommsUpdatesHeaderManager,
} from '@bark/bark-core';
import { BarkConnect } from '../components/bark-connect/bark_connect';
import { SetupBrowserPushManager } from '../components/browser-push-manager/browser-push-manager';
import { SellerAvatarFallback } from '../components/legacy-scripts/avatar/seller-avatar-fallback';
import { exposeSellerFunctionsToWindow } from '../components/responses/booking-cancellation-modal';
export const routerUrl = () => {
	const router = new Navigo('/');

	router.hooks({
		before: async (done) => {
			SellerNotificationsV2();
			SellerCommsUpdatesHeaderManager();
			SetupBrowserPushManager();
			SellerAvatarFallback();
			done();
		},
	});

	router
		.notFound(async () => {
			// called when there is path specified but
			// there is no route matching
		})
		.on('/sellers/home', async () => {
			await useNamespace(['seller_homepage']);

			const { HomePage } = await import('../pages');
			HomePage();
		})
		.on('/sellers/my-barks', async () => {
			await useNamespace([
				'seller_common',
				'common_activity-log',
				'seller_templates',
				'seller_responses',
				'seller_response_flow',
				'seller_bark-connect',
				'seller_send-estimate-modal',
				'seller_dashboard-email',
				'seller_sms-template-manager',
			]);
			BarkConnect();

			const { ResponsesPage } = await import('../pages');
			ResponsesPage();
			exposeSellerFunctionsToWindow();
		})
		.on('/sellers/dashboard/', async () => {
			await useNamespace([
				'seller_common',
				'seller_dashboard',
				'seller_responses',
				'seller_templates',
				'seller_sms-template-manager',
				'seller_settings_elite-pro',
				'seller_dashboard_profile-completeness',
				'seller_bark-connect',
				'seller_dashboard-email',
				'seller_send-estimate-modal',
				'seller_response_flow',
				'seller_settings_upsell-modal',
				'seller_leads-dashboard',
			]);

			BarkConnect();

			const { DashboardPage } = await import('../pages');
			DashboardPage();
		})
		.on('/sellers/offer/', async () => {
			const { OfferPage } = await import('../pages');
			await useNamespace(['seller_common', 'seller_payments', 'seller_settings_credits']);
			await OfferPage();
		})
		.on('/sellers/bookable-services/*', async () => {
			const { TRXJobs } = await import('../pages');
			await TRXJobs();
		})
		.on('/sellers/signup-offer/', async () => {
			const { OfferPage } = await import('../pages');
			await useNamespace(['seller_common', 'seller_payments', 'seller_settings_credits']);
			await OfferPage();
		})
		.on('/settings/credits_subscription/subscribe/', async () => {
			const { CsSubscriptionPage } = await import('../pages');
			await useNamespace(['seller_common']);
			CsSubscriptionPage();
		})
		.on('/settings/credits_subscription/subscribe/:uuid', async ({ data }) => {
			const { CsSubscriptionPage } = await import('../pages');
			await useNamespace(['seller_common']);
			CsSubscriptionPage(data.uuid);
		})
		.on('/settings/profile/my_profile/', async () => {
			await useNamespace([
				'seller_settings_pricing',
				'seller_settings_profile',
				'seller_settings_photo-video',
				'seller_settings_services',
				'seller_settings_pricing',
				'seller_settings_accreditation',
				'common_form-manager',
			]);

			const { MyProfile } = await import('../pages');
			MyProfile();
		})
		.on('/settings/profile/elite_pro/', async () => {
			await useNamespace([
				'seller_settings_elite-pro-subscribed',
				'seller_settings_elite-pro',
				'seller_common',
				'seller_settings_credits',
			]);
			const { EliteProSettings } = await import('../pages/settings');

			EliteProSettings();
		})
		.on('/settings/profile/account_details/', async () => {
			await useNamespace(['seller_settings_account-details', 'common_form-manager']);
			const { AccountDetailsSettings } = await import('../pages/settings');
			AccountDetailsSettings();
		})
		.on('/settings/profile/badges/', async () => {
			await useNamespace('seller_settings_badges');
			const { BadgesSettings } = await import('../pages/settings');
			BadgesSettings();
		})
		.on('/settings/leads/my_services/*', async () => {
			await useNamespace([
				'seller_settings_lead-preferences',
				'seller_settings_services',
			]);
			const { MyServicesSettings } = await import('../pages/settings');
			MyServicesSettings();
		})
		.on('/settings/communication/one_click_response/', async () => {
			await useNamespace([
				'seller_settings_comms',
				'seller_common',
				'common_form-manager',
			]);

			const { OneClickResponseSettings } = await import('../pages/settings');
			OneClickResponseSettings();
		})
		.on('/settings/communication/email_templates/', async () => {
			await useNamespace(['seller_settings_comms']);

			const { EmailTemplateSettings } = await import('../pages/settings');
			EmailTemplateSettings();
		})
		.on('/settings/communication/sms_templates/', async () => {
			await useNamespace(['seller_settings_comms', 'seller_sms-template-manager']);
			const { SMSTemplateSettings } = await import('../pages/settings');
			SMSTemplateSettings();
		})
		.on('/settings/integrations/hubspot/', async () => {
			await useNamespace(['common_form-manager', 'seller_settings_hubspot']);
			const { HubspotSettings } = await import('../pages/settings');
			HubspotSettings();
		})
		.on('/settings/integrations/zapier/', async () => {
			const { ZapierSettings } = await import('../pages/settings');
			ZapierSettings();
		})
		.on('/settings/billing/my_credits/', async () => {
			await useNamespace([
				'seller_common',
				'seller_settings_credits',
				'seller_settings_elite-pro',
				'seller_settings_upsell-modal',
			]);
			const { MyCreditsSettings } = await import('../pages/settings');
			MyCreditsSettings();
		})
		.on('/settings/billing/billing_details/', async () => {
			await useNamespace([
				'seller_settings_billing',
				'seller_settings_account-details',
				'seller_common',
				'common_form-manager',
			]);

			const { BillingDetailsSettings } = await import('../pages/settings');
			BillingDetailsSettings();
		})
		.on('/settings/billing/saved_card/', async () => {
			await useNamespace([
				'seller_settings_billing',
				'seller_settings_credits',
				'seller_common',
			]);

			const { PaymentDetailsSettings } = await import('../pages/settings');
			PaymentDetailsSettings();
		})
		.on('/settings/notifications/email_notifications/', async () => {
			await useNamespace(['seller_settings_email-notifications', 'common_form-manager']);

			const { EmailSettings } = await import('../pages/settings');
			EmailSettings();
		})
		.on('/settings/notifications/browser/', async () => {
			await useNamespace([
				'seller_settings_browser-notifications',
				'common_form-manager',
			]);
			const { BrowserNotificationsSettings } = await import('../pages/settings');
			BrowserNotificationsSettings();
		})
		.on('/:lang/:locale/feedback/questions/:cat/:action/*', async () => {
			await useNamespace(['seller_feedback-questions']);
			FeedbackQuestionsPage();
		})
		.on('/sellers/updates/', async () => {
			await useNamespace('seller_updates');
			listUpdatePage();
		})
		.on('/settings/', async () => {
			SettingsPage();
		})
		.resolve();
};
